-- | Step 3 fill-queue load/save helpers shared by the app-owned runtime.
module Steps.Step3.FillQueue
    ( loadInitFillQueue
    , loadFillState
    , saveDocument
    , shouldSaveDocument
    , applySavedDocument
    , mergeTransitionWaitState
    ) where

import Common

import API.Account (AccountInfo, AccountType(..))
import API.Document (Document, updateDocument)
import API.FormBase (FormBasic, FormID)
import API.FormType (FormType)
import API.RecentForms (RecentForm)
import API.Types (StudentID)
import App.Route as AppRoute
import Control.Monad.Except (runExceptT)
import Control.Monad.Except.Trans (ExceptT(..), except)
import Data.Argonaut as JSON
import Data.Array as Arr
import Data.Either (note)
import Data.Tuple (Tuple(..))
import Error (ErrorWrapper)
import GraphQL.Join (runGraphQL_)
import GraphQL.Type (innerId)
import Steps.Step3.Transition (TransitionState)
import Steps.Step3.Types (FillQueueBase, FillQueueState, FormFillState, currentFillEntry, hasEditableFields, matchesLookup)
import Steps.WorkQueue.Cache (WorkQueueCache, getDoc, getDocByStudent, getDocument, getForm, getFormType, insertDocument, newDocument)
import Steps.WorkQueue.State (FillQueueEntry, FormSectionID, WorkQueueState, getFillQueue, getSemesterOrErr, getTeacherOrErr, setCurrentDoc, setTeacherAndSemester, showFillQueue)
import Steps.WorkQueue.UIState (WorkQueueUiState)
import Widgets.Attachments (initAttachments)
import Widgets.BetterSelect (setSelected)
import Widgets.Comments (initComments)
import Widgets.Form (FormState, formData, fromFormType, setFormData)
import Widgets.Form.Class (FormContext, initFormContext)
import Widgets.FormPreview (initDocPreview)
import Widgets.Signature (LoadDefaultsOp(..), fromDocSigField, fromFormSigField, loadDefaults)
import Widgets.Signature.Group (SigGroupState, initSigGroup, loadSignatures)

loadInitFillQueue
    :: WorkQueueState
    -> WorkQueueUiState
    -> WorkQueueCache
    -> Maybe Document
    -> TransitionState
    -> Maybe FormSectionID
    -> Aff (Either ErrorWrapper FillQueueState)
loadInitFillQueue workQueueState workQueueUi workQueueCache doc transitionState anchor = runExceptT do
    let queue = getFillQueue workQueueState
    let position = initialQueuePosition workQueueState queue
    ExceptT $ loadFillState
        { queue
        , transitionState
        , workQueueState
        , workQueueUi
        , workQueueCache
        , position
        , anchor
        , activeSection: anchor
        , sectionIntent: maybe AppRoute.SectionClear AppRoute.SectionSet anchor
        }
        doc

saveDocument :: FillQueueState -> Aff (Either ErrorWrapper Document)
saveDocument { fillState: { form, doc }, workQueueState } = runExceptT do
    let json = formData form
    teacher <- except $ getTeacherOrErr workQueueState
    semester <- except $ getSemesterOrErr workQueueState
    runGraphQL_ $ updateDocument doc json
        { teacher: teacher.id, semester: semester.semester }

shouldSaveDocument :: FillQueueState -> Boolean
shouldSaveDocument state =
    hasEditableFields state.fillState && hasUnsavedDocumentChanges state

applySavedDocument :: FillQueueState -> Document -> Either ErrorWrapper FillQueueState
applySavedDocument state doc = do
    let workQueueState' = insertDocument doc state.workQueueState
    let formCtx = initFormContext state.workQueueCache.account doc
    let fillState = state.fillState
            { form = state.fillState.form { access = doc.access }
            }
    nextState <- finaliseFillState (state { workQueueState = workQueueState' }) formCtx doc fillState
    pure $ nextState
        { fillState
            { signatures = loadSignatures nextState.fillState.doc state.fillState.signatures
            }
        }

mergeTransitionWaitState :: FillQueueState -> FillQueueState -> FillQueueState
mergeTransitionWaitState waitState loadedState =
    let
        workQueueUi = loadedState.workQueueUi
            { expandedStudents = waitState.workQueueUi.expandedStudents
            }
    in
    loadedState
        { transitionState = waitState.transitionState
        , workQueueUi = workQueueUi
        }

loadFillState :: forall r. FillQueueBase r -> Maybe Document -> Aff (Either ErrorWrapper FillQueueState)
loadFillState state doc = runExceptT do
    entry <- except $ note (StringError $ "Position in fill queue out of bounds. Index: " <> show state.position <> " Queue: " <> showFillQueue state.queue) $
        currentFillEntry state
    case entry.form of
        Left recentForm -> loadExistingFillState state recentForm state.workQueueState doc
        Right formId -> do
            let docRef =
                    { formId
                    , studentId: entry.student
                    , documentId: Nothing
                    }
            case getDocByStudent state.workQueueState docRef of
                Just doc0 -> loadPreviousFillState state doc0 formId state.workQueueState
                Nothing -> createDocFormFillState state formId entry.student

initialQueuePosition :: WorkQueueState -> Array FillQueueEntry -> Int
initialQueuePosition workQueueState queue = case workQueueState.currentDoc of
    Nothing -> 0
    Just lookup -> fromMaybe 0 $ Arr.findIndex (matchesLookup lookup) queue

hasUnsavedDocumentChanges :: FillQueueState -> Boolean
hasUnsavedDocumentChanges state =
    let nextJson = formData form
        jsonChanged = nextJson /= doc.json
        teacherChanged = teacherUnsavedChanges state
        semesterChanged = semesterUnsavedChanges state
        changed = jsonChanged || teacherChanged || semesterChanged
    in changed
    where
    form = state.fillState.form
    doc = state.fillState.doc

    teacherUnsavedChanges :: FillQueueState -> Boolean
    teacherUnsavedChanges { fillState: { doc: currentDoc }, workQueueState } =
        (map _.id workQueueState.teacher) /= (map _.id currentDoc.teacher)

    semesterUnsavedChanges :: FillQueueState -> Boolean
    semesterUnsavedChanges { fillState: { doc: currentDoc }, workQueueState } =
        (map _.semester workQueueState.semester) /= Just currentDoc.semester.semester

loadPreviousFillState
    :: forall r
     . FillQueueBase r
    -> Document
    -> FormID
    -> WorkQueueState
    -> ExceptT ErrorWrapper Aff FillQueueState
loadPreviousFillState state doc formId workQueueState = do
    ftype <- except $ note (StringError $ "Unknown formtype " <> innerId formId) $
        getForm workQueueState formId
    let synced = syncDocMetadata doc workQueueState state.workQueueUi
    let nextBase = state { workQueueState = synced.state, workQueueUi = synced.ui }
    fillState <- loadFormFillLayout nextBase ftype doc
    let formCtx = initFormContext state.workQueueCache.account doc
    let currentBase = nextBase
            { workQueueState = setCurrentDoc nextBase.workQueueState $ Just
                { formId
                , studentId: doc.student.id
                , documentId: Just doc.id
                }
            }
    except $ finaliseFillState currentBase formCtx doc fillState

createDocFormFillState
    :: forall r
     . FillQueueBase r
    -> FormID
    -> StudentID
    -> ExceptT ErrorWrapper Aff FillQueueState
createDocFormFillState state formId student = do
    let workQueueState = state.workQueueState
    ftype <- except $ note (StringError $ "Unknown formtype " <> innerId formId) $
        getForm workQueueState formId
    semester <- except $ getSemesterOrErr workQueueState
    teacher <- if state.workQueueCache.account.type /= BoardAccount
        then map Just $ except $ getTeacherOrErr workQueueState
        else pure Nothing
    Tuple newWorkQueueState doc <- newDocument workQueueState ftype
        { student_id: student, semester: semester.semester, teacher_id: _.id <$> teacher }
    let nextBase = state
            { workQueueState = setCurrentDoc newWorkQueueState $ Just
                { formId
                , studentId: doc.student.id
                , documentId: Just doc.id
                }
            }
    fillState <- loadFormFillLayout nextBase ftype doc
    let formCtx = (initFormContext state.workQueueCache.account doc) { isAutofilling = true }
    except $ finaliseFillState nextBase formCtx doc fillState

loadExistingFillState
    :: forall r
     . FillQueueBase r
    -> RecentForm
    -> WorkQueueState
    -> Maybe Document
    -> ExceptT ErrorWrapper Aff FillQueueState
loadExistingFillState state { id, form_type: ftype } workQueueState maybeDoc = do
    let cachedDoc = getDoc workQueueState id
    Tuple newWorkQueueState' doc <- case maybeDoc, cachedDoc of
        Just doc, _ | doc.id == id -> pure $ Tuple (insertDocument doc workQueueState) doc
        _, Just doc -> pure $ Tuple (insertDocument doc workQueueState) doc
        _, Nothing -> getDocument workQueueState ftype id
    let synced = syncDocMetadata doc newWorkQueueState' state.workQueueUi
    let nextBase = state
            { workQueueState = setCurrentDoc synced.state $ Just
                { formId: ftype.id
                , studentId: doc.student.id
                , documentId: Just doc.id
                }
            , workQueueUi = synced.ui
            }
    fillState <- loadFormFillLayout nextBase ftype doc
    let formCtx = initFormContext state.workQueueCache.account doc
    except $ finaliseFillState nextBase formCtx doc fillState

-- | Load document metadata into the workqueue state and select UI.
-- |
-- | User edits are tracked in `WorkQueueState` and compared back to
-- | `doc` when deciding whether to save.
syncDocMetadata
    :: Document
    -> WorkQueueState
    -> WorkQueueUiState
    -> { state :: WorkQueueState, ui :: WorkQueueUiState }
syncDocMetadata doc workQueueState workQueueUi =
    { state: setTeacherAndSemester workQueueState doc.teacher doc.semester
    , ui:
        workQueueUi
            { teacher = setSelected workQueueUi.teacher $ map teacherOption doc.teacher
            , semester = setSelected workQueueUi.semester $ Just $ semesterOption doc.semester
            }
    }
  where
    teacherOption teacher = { label: teacher.name, value: teacher }
    semesterOption semester = { label: semester.name, value: semester }

loadFormFillLayout
    :: forall r
     . FillQueueBase r
    -> FormBasic
    -> Document
    -> ExceptT ErrorWrapper Aff FormFillState
loadFormFillLayout state fbasic doc = do
    ftype <- except $ note (StringError $ "FormType " <> innerId fbasic.id <> " wasn't loaded") $
        getFormType state.workQueueCache fbasic.id
    let form = (fromFormType ftype) { access = doc.access }
    pure
        { form
        , preview: initDocPreview doc
        , signatures: loadSignatures doc $ initFormSigs state.workQueueCache.account ftype doc
        , comments: initComments
            { id: "form-comments"
            , formId: doc.id
            , comments: doc.comments
            , can: doc.can
            }
        , attachments: initAttachments
            { id: "form-attachments"
            , formId: doc.id
            , attachments: doc.attachments
            }
        , doc
        }

finaliseFillState
    :: forall r
     . FillQueueBase r
    -> FormContext
    -> Document
    -> FormFillState
    -> Either ErrorWrapper FillQueueState
finaliseFillState state formCtx doc fillState = do
    form <- setFormData formCtx doc.json fillState.form
    let Tuple workQueueState normalizedDoc = normaliseDocument state.workQueueState doc form
    pure
        { queue: state.queue
        , position: state.position
        , anchor: state.anchor
        , activeSection: state.activeSection
        , sectionIntent: state.sectionIntent
        , transitionState: state.transitionState
        , workQueueState
        , workQueueUi: state.workQueueUi
        , workQueueCache: state.workQueueCache
        , fillState: fillState
            { form = form
            , doc = normalizedDoc
            }
        }
  where
    normaliseDocument :: WorkQueueState -> Document -> FormState -> Tuple WorkQueueState Document
    normaliseDocument workQueueState sourceDoc form =
        let normalizedDoc = sourceDoc { json = formData form }
        in Tuple (insertDocument normalizedDoc workQueueState) normalizedDoc

initFormSigs :: AccountInfo -> FormType -> Document -> SigGroupState
initFormSigs account ft doc = initSigGroup account "form-signatures" doc.id $
    if Arr.null doc.signature_fields
        then map (fromFormSigField doc.id) ft.signatures
        else map initSig doc.signature_fields
  where
    initSig field = loadDefaults AuthoritativeOp (fromDocSigField doc.id field) field

foreign import debugJsonDiffSummary :: JSON.Json -> JSON.Json -> Array String
